import { useEffect, useRef, useState, useCallback } from 'react';

interface UseCardStaggerOptions {
  /** Delay between each card in ms (default 70) */
  staggerMs?: number;
  /** IntersectionObserver threshold (default 0.05) */
  threshold?: number;
  /** Reset animation when key changes */
  resetKey?: string | number;
}

/**
 * Returns:
 *  - containerRef: attach to the grid/list wrapper
 *  - visibleCount: how many cards should currently be visible
 *  - isReady: true once the container entered the viewport
 *  - getDelay: (index) => inline transitionDelay string
 */
export function useCardStagger(
  count: number,
  options: UseCardStaggerOptions = {},
) {
  const { staggerMs = 70, threshold = 0.05, resetKey } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startStagger = useCallback(() => {
    setVisibleCount(0);
    setIsReady(true);
    let idx = 0;
    const tick = () => {
      idx += 1;
      setVisibleCount(idx);
      if (idx < count) timerRef.current = setTimeout(tick, staggerMs);
    };
    timerRef.current = setTimeout(tick, 40);
  }, [count, staggerMs]);

  /* Re-run stagger when resetKey (filter) changes */
  useEffect(() => {
    if (!isReady) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisibleCount(0);
    let idx = 0;
    const tick = () => {
      idx += 1;
      setVisibleCount(idx);
      if (idx < count) timerRef.current = setTimeout(tick, staggerMs);
    };
    timerRef.current = setTimeout(tick, 60);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  /* First-time viewport entry */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          obs.disconnect();
          startStagger();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [startStagger, threshold]);

  /* Cleanup on unmount */
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const getDelay = (index: number) => `${index * staggerMs}ms`;

  return { containerRef, visibleCount, isReady, getDelay };
}